import { useRef, useState } from 'react';
import { parseAnimation } from '@game-platform/pack-animation';
import { Button } from '../../components/ui/Button';
import { useKeepFocusWhenGone } from '../../components/ui/useKeepFocusWhenGone';
import type { PoolOfferConfig } from '../../lib/api/operatorConsole';
import { packTitle } from '../Animation/animationScope';
import { mechanicTitle } from '../Animation/mechanics';
import type { GlobalAnimation } from '../Animation/useGlobalAnimation';

type Props = {
  pack: PoolOfferConfig;
  /** What packs without their own opening play; null when unknown. */
  global: GlobalAnimation | null;
  /** The global animation is still being read: the editor would start from a guess. */
  loading: boolean;
  onEdit: () => void;
  onReset: () => Promise<void>;
};

/** The pack's opening at a glance; editing happens in PackOpeningModal. */
export function OpeningAnimationSetting({ pack, global, loading, onEdit, onReset }: Props) {
  const [resetting, setResetting] = useState(false);
  const own = pack.animation ? parseAnimation(pack.animation) : null;
  const shown = own ?? global?.animation;
  const title = packTitle(pack);
  const action = own ? 'Edit' : 'Customize';
  const actionRef = useRef<HTMLButtonElement>(null);
  // "Use global animation" goes away once it worked
  useKeepFocusWhenGone(Boolean(own), actionRef);

  async function reset() {
    if (!window.confirm(`${title} will play the global animation again.`)) return;
    setResetting(true);
    try {
      await onReset();
    } finally {
      setResetting(false);
    }
  }

  return (
    <section aria-label="Opening animation" className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <h3 className="text-[12px] font-medium text-[var(--co-text)]">Opening animation</h3>
        {own && (
          <>
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
              custom
            </span>
            <button
              type="button"
              onClick={() => void reset()}
              disabled={resetting}
              aria-label={`Use global animation for ${title}`}
              className="text-[11px] text-[var(--co-muted)] underline-offset-2 hover:text-[var(--co-text)] hover:underline"
            >
              Use global animation
            </button>
          </>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 rounded border border-[var(--co-border)] bg-[var(--co-bg)] px-3 py-2">
        <div className="min-w-0">
          <div className="text-[13px] text-[var(--co-text)]">
            <span className="font-medium">{shown ? mechanicTitle(shown.variant) : 'Global animation'}</span>
            <span className="text-[var(--co-muted)]">
              {own ? ' · this pack only' : shown ? ' · global animation' : ''}
            </span>
          </div>
          <p className="text-[11px] text-[var(--co-muted)]">
            {own
              ? 'Packs without their own keep the global animation.'
              : 'Same as every pack without its own. Customize to give this pack a different one.'}
          </p>
        </div>
        <Button
          ref={actionRef}
          variant="ghost"
          disabled={loading || resetting}
          onClick={onEdit}
          aria-label={`${action} opening animation of ${title}`}
          className="shrink-0 px-3 py-1.5 text-[12px]"
        >
          {action}
        </Button>
      </div>
    </section>
  );
}
